import { useEffect, useRef, useState } from "react";
import { useAuth } from "./auth";
import { supabase } from "./supabase";

export function useBetaAgreement(): { accepted: boolean; loading: boolean } {
  const { user, loading: authLoading } = useAuth();
  const [accepted, setAccepted] = useState(false);
  const [checking, setChecking] = useState(false);
  // Cache the verified user ID so token refreshes (which change the user
  // object reference but keep the same user.id) don't flash loading state.
  // A loading flash in ProtectedRoute unmounts the entire page tree,
  // destroying component state (e.g. mining chat view, drafts).
  const verifiedUserIdRef = useRef<string | null>(null);

  // Synchronous flag bridges the gap between auth completing and the effect
  // firing, preventing a render frame where both auth and beta report
  // "not loading" before the beta fetch has started.
  const needsCheck = !authLoading && !!user && verifiedUserIdRef.current !== user.id;

  useEffect(() => {
    if (authLoading) return;

    if (!user) {
      verifiedUserIdRef.current = null;
      setAccepted(false);
      setChecking(false);
      return;
    }

    // Already verified for this user — skip the re-check entirely
    if (verifiedUserIdRef.current === user.id) return;

    setChecking(true);
    const userId = user.id;
    supabase
      .from("user_profiles")
      .select("beta_agreement_accepted_at")
      .eq("id", userId)
      .maybeSingle()
      .then(({ data }) => {
        verifiedUserIdRef.current = userId;
        setAccepted(data?.beta_agreement_accepted_at != null);
        setChecking(false);
      });
  }, [user, authLoading]);

  // Single loading signal: covers auth loading, the sync gap before the
  // effect fires, and the async beta-agreement fetch itself.
  return { accepted, loading: authLoading || needsCheck || checking };
}

export async function acceptBetaAgreement(): Promise<void> {
  const {
    data: { user },
  } = await supabase.auth.getUser();
  if (!user) throw new Error("Not authenticated");

  const { error } = await supabase
    .from("user_profiles")
    .upsert(
      { id: user.id, beta_agreement_accepted_at: new Date().toISOString() },
      { onConflict: "id" },
    )
    .select()
    .single();
  if (error) throw error;
}
